/* eslint-disable @typescript-eslint/naming-convention -- Matomo / tracker custom attribute keys */
import { allTrackers } from "app/services/track/track";
import type { track as TrackType } from "pro-features/components/WithTrack/src/context";

export type HomepageInsightTrackItem = {
    insightNumber: number;
    fullText: string;
};

export type HomePageInsightsTrackingEntity = {
    trackInsightsSectionView: (params: { insightCount: number; insights: HomepageInsightTrackItem[] }) => void;
    trackInsightExpand: () => void;
    trackInsightCollapse: () => void;
    trackInsightRowClick: (params: { insightNumber: number; fullText: string }) => void;
    trackInsightRowClose: (params: { insightNumber: number; fullText: string }) => void;
    trackInsightCopy: (fullText: string) => void;
    trackInsightThumbsUp: (fullText: string) => void;
    trackInsightThumbsDown: (fullText: string) => void;
    trackInsightFeedbackModalView: () => void;
    trackInsightFeedbackModalSubmit: (feedbackText: string) => void;
    trackInsightFeedbackModalDismiss: () => void;
};

const buildNumberedInsightAttributes = (insights: HomepageInsightTrackItem[]) => {
    const attributes: Record<string, string> = {};

    insights.forEach(({ insightNumber, fullText }) => {
        attributes[`insight_${String(insightNumber)}_full_text`] = fullText;
    });

    return attributes;
};

const createHomePageInsightsTracking = (track: TrackType): HomePageInsightsTrackingEntity => ({
    trackInsightsSectionView({ insightCount, insights }) {
        track("Section", "view", "Insights", {
            number_of_insights: insightCount,
            ...buildNumberedInsightAttributes(insights),
        });
    },

    trackInsightExpand() {
        track("Button", "open", "Insights");
    },

    trackInsightCollapse() {
        track("Button", "close", "Insights");
    },

    trackInsightRowClick({ insightNumber, fullText }) {
        track("Button", "click", "specific Insights", {
            insight_number: insightNumber,
            full_text: fullText,
        });
    },

    trackInsightRowClose({ insightNumber, fullText }) {
        track("Button", "close", "specific Insights", {
            insight_number: insightNumber,
            full_text: fullText,
        });
    },

    trackInsightCopy(fullText) {
        track("Button", "click", "Copy insight", {
            full_text: fullText,
        });
    },

    trackInsightThumbsUp(fullText) {
        track("Button", "click", "insight Feedback/thumbs up", {
            full_text: fullText,
        });
    },

    trackInsightThumbsDown(fullText) {
        track("Button", "click", "insight Feedback/thumbs down", {
            full_text: fullText,
        });
    },

    trackInsightFeedbackModalView() {
        track("popup", "view", "insight Feedback/feedback modal");
    },

    trackInsightFeedbackModalSubmit(feedbackText) {
        track("popup", "submit", "insight Feedback/feedback modal", {
            feedback_text: feedbackText,
        });
    },

    trackInsightFeedbackModalDismiss() {
        track("popup", "close", "insight Feedback/feedback modal");
    },
});

export const homePageInsightsTracking = createHomePageInsightsTracking(
    allTrackers.dispatchTrackEvent.bind(allTrackers) as TrackType,
);
